import { Radio } from 'lucide-react';

import { useSyncStore } from '@/stores/sync-store';
import type { SyncStatus } from '@/types/sync';

interface StatusConfig {
  iconColor: string;
  label: string;
  labelColor: string;
  /** CSS animation class — only for syncing state */
  animate?: string;
}

function getStatusConfig(status: SyncStatus): StatusConfig {
  switch (status) {
    case 'syncing':
      return {
        iconColor: '#c8651a',
        label: 'SYNCING',
        labelColor: 'text-[var(--color-text-muted)]',
        animate: 'animate-pulse',
      };
    case 'synced':
      return {
        iconColor: '#4a8c5c',
        label: 'SYNCED',
        labelColor: 'text-[#4a8c5c]',
      };
    case 'offline':
      return {
        iconColor: '#9a9590',
        label: 'OFFLINE',
        labelColor: 'text-[var(--color-text-muted)]',
      };
    case 'error':
      return {
        iconColor: '#ef4444',
        label: 'SYNC ERROR',
        labelColor: 'text-red-500',
      };
    default: // 'idle'
      return {
        iconColor: '#4a8c5c',
        label: '',
        labelColor: 'text-[var(--color-text-muted)]',
      };
  }
}

/**
 * Compact sync status indicator for the app header.
 * Instrument-aesthetic: radio icon + uppercase 10px label.
 * Shows nothing when idle (no sync has run yet).
 */
export function SyncStatusBadge() {
  const { status } = useSyncStore();

  if (status === 'idle') return null;

  const { iconColor, label, labelColor, animate } = getStatusConfig(status);

  return (
    <div
      className={`flex items-center gap-1 ${labelColor}`}
      aria-label={`Sync status: ${label || status}`}
      data-testid="sync-status-badge"
      data-status={status}
    >
      <span
        data-testid="sync-status-icon"
        className={`flex items-center${animate ? ` ${animate}` : ''}`}
        aria-hidden
        data-animating={animate ? 'true' : undefined}
        style={{ color: iconColor, flexShrink: 0 }}
      >
        <Radio size={12} />
      </span>
      {label && (
        <span
          className="hidden sm:inline text-[10px] font-bold uppercase tracking-widest"
          data-testid="sync-status-label"
        >
          {label}
        </span>
      )}
    </div>
  );
}
